import { CornerUpLeft, LoaderCircle, PanelRightOpen, X } from 'lucide-react';
import { useTaskDetail } from '../../../entities/task';
import { useWorkOrderDetail } from '../../../entities/work-order';
import { Input } from '../../../shared/ui';
import { formatBoardMinutes, isWeekend } from '../../../shared/lib/schedule';
import { PLAN_LABELS, type PlanningDraftState } from '../model/draft';
import { formatPlanDay, planSummary } from '../model/planSummary';
import type { DispositionMode } from '../model/useDispositionMode';
import type { RescheduleSource } from '../model/useRescheduleSource';

interface Props {
  plan: DispositionMode;
  dayIso: string;
  /** The Termin being moved, when this is a reschedule rather than a placement. */
  source: RescheduleSource | null;
  /** Jump the board back to `source.dayIso`. Omitted when there is nowhere to jump. */
  onGoToSourceDay?: () => void;
  draft: PlanningDraftState | null;
  /** Name of the technician whose row was clicked, for the summary line. */
  pickedTechnicianName: string | null;
  /** The draft overlaps something — advisory, it does not block the button. */
  colliding: boolean;
  /**
   * The draft sits outside the technician's shift — also advisory. Saving raises the API's
   * `Außerhalb der Arbeitszeit` dialog, which offers "Trotzdem planen", so the button stays enabled.
   */
  outsideShift: boolean;
  /** Saves the draft exactly as drawn — the primary half of the button. */
  onSave: () => void;
  /**
   * Opens the docked form — the "Details" half. Prefilled from the draft when there is one; without
   * one the form opens on its defaults and picking a row on the board fills it in.
   */
  onOpenForm: () => void;
  /** False while the draft is missing, something it needs is still loading, or a title is empty. */
  canSave: boolean;
  isSaving: boolean;
  /** Title of a brand-new Aufgabe. Only shown, and only required, in that mode. */
  taskTitle: string;
  onTaskTitleChange: (value: string) => void;
  /**
   * The week is on screen: nothing can be placed there, so the slot line says to pick a day instead
   * of inviting a click into a technician's row that does not exist.
   */
  isWeekView?: boolean;
}

/**
 * The planning bar: what is being placed, where it would go, and the split button that commits it.
 *
 * The primary half saves the draft exactly as the board shows it — most of the time everything has
 * already been said by clicking and dragging. The narrow half opens the docked form, prefilled, for
 * the cases that need a description, attachments or a series.
 *
 * Deliberately thin. It carries no duration steppers — the durations are shaped by dragging the
 * draft's handles on the board, and the form owns the other fields.
 */
export const PlanningBar = ({
  plan,
  dayIso,
  source,
  onGoToSourceDay,
  draft,
  pickedTechnicianName,
  colliding,
  outsideShift,
  onSave,
  onOpenForm,
  canSave,
  isSaving,
  taskTitle,
  onTaskTitleChange,
  isWeekView = false,
}: Props) => {
  const labels = plan.mode ? PLAN_LABELS[plan.mode] : null;

  // Rescheduling names the Auftrag the same way creating does — the appointment the source hook
  // already loaded is only the detour that gets us its work order. Null in task mode: an Aufgabe
  // has no Auftrag, so this query stays disabled for the whole of it.
  const workOrderId = plan.workOrderId ?? source?.workOrderId ?? undefined;

  const workOrder = useWorkOrderDetail(workOrderId);
  const task = useTaskDetail(plan.taskId ?? undefined);

  const isRescheduling = source != null;
  // An Aufgabe nobody created yet: `POST /tasks` needs a title the board cannot supply.
  const isNewTask = plan.mode === 'schedule-task' && plan.taskId == null;
  const primaryLabel = isRescheduling
    ? labels?.move
    : isNewTask
      ? 'Aufgabe anlegen'
      : labels?.action;

  /**
   * Every flag is paired with "did we actually ask for this one".
   *
   * `isPending` means *no data*, not *fetching*, so a **disabled** query reports it forever — it has
   * nothing and never will. Reading `workOrder.isPending` unguarded left the bar on its skeleton for
   * the whole of task mode, where that query is never enabled.
   */
  const isLoading =
    (source?.isPending ?? false) ||
    (workOrderId != null && workOrder.isPending) ||
    (plan.taskId != null && task.isPending);

  // The clock time the form will receive — the WORK start, i.e. after the Anfahrt.
  const slotLine = isWeekView
    ? 'Wählen Sie einen Tag — dort legen Sie den Zeitpunkt fest.'
    : draft && pickedTechnicianName != null
      ? [
          pickedTechnicianName,
          formatPlanDay(dayIso),
          formatBoardMinutes(draft.startMin + draft.durations.travel),
          `${draft.durations.travel}+${draft.durations.work}+${draft.durations.departure} min`,
        ].join(' · ')
      : 'Klicken Sie in die Zeile eines Handwerkers – oder öffnen Sie die Details.';

  /**
   * Where the Termin is *today*. Stated in words rather than left to the block, because the block is
   * hidden while it is being moved and is on another day the moment the dispatcher looks around.
   */
  const currentLine =
    source && source.dayIso
      ? [formatPlanDay(source.dayIso), source.timeLabel, source.technicianName]
          .filter(Boolean)
          .join(' · ')
      : null;

  // Nowhere to jump when the board is already there, and a weekend source would only be bounced
  // back to Monday by `useDispositionDay` — so the line stays plain text and keeps telling the truth.
  const canJumpToSource =
    source != null && source.dayIso !== '' && source.dayIso !== dayIso && !isWeekend(source.dayIso);

  return (
    <div className="z-40 flex shrink-0 flex-wrap items-center gap-x-6 gap-y-3 border-t border-slate-200 bg-white px-6 py-4 shadow-[0_-2px_12px_rgba(0,0,0,0.06)]">
      {/* A real flex-basis, not `flex-1` (basis 0): with 0 the column never wraps, it just shrinks to
          nothing when the controls are wide — which is what happened beside a docked panel. */}
      <div className="min-w-0 flex-[1_1_18rem]">
        {isLoading ? (
          <div className="animate-pulse space-y-2">
            <div className="h-4 w-56 rounded bg-gray-200" />
            <div className="h-3 w-40 rounded bg-gray-100" />
          </div>
        ) : (
          <>
            <p className="truncate font-['PT_Sans'] text-[18px] font-bold leading-tight text-[#004494]">
              {planSummary(plan, workOrder.data, task.data)}
            </p>

            {currentLine &&
              (canJumpToSource ? (
                <button
                  type="button"
                  onClick={onGoToSourceDay}
                  title="Zum aktuellen Termin springen"
                  className="mt-1 flex max-w-full items-center gap-1 rounded text-sm text-[#004494] underline-offset-2 transition hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#004494]"
                >
                  <CornerUpLeft className="h-3.5 w-3.5 shrink-0" strokeWidth={2.5} aria-hidden />
                  <span className="truncate">Aktuell: {currentLine}</span>
                </button>
              ) : (
                <p className="mt-1 truncate text-sm text-zinc-500">Aktuell: {currentLine}</p>
              ))}

            <p className="mt-1 truncate text-sm text-zinc-500">{slotLine}</p>
          </>
        )}
      </div>

      {colliding && draft && (
        <p
          className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600"
          role="alert"
        >
          Überschneidung mit einem bestehenden Eintrag
        </p>
      )}

      {/* Amber, and shown next to the red pill rather than instead of it: the two are independent
          and a draft can be both booked over and off shift. The wording is the title of the dialog
          the save will raise, so the warning and the confirmation say the same thing. */}
      {outsideShift && draft && (
        <p
          className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700"
          role="alert"
        >
          Außerhalb der Arbeitszeit
        </p>
      )}

      {/* Own row below the summary when the bar is narrow; beside it when there is room. A new
          Aufgabe carries the title field, so its group gets a wider basis and may wrap in itself. */}
      <div
        className={[
          'ml-auto flex items-center gap-3',
          isNewTask ? 'flex-[1_1_28rem] flex-wrap justify-end' : 'shrink-0',
        ].join(' ')}
      >
        {isNewTask && (
          <Input
            className="min-w-[12rem] flex-[1_1_14rem]"
            inputClassName="!h-12 !min-h-12"
            placeholder="Titel der Aufgabe"
            aria-label="Titel der Aufgabe"
            value={taskTitle}
            onChange={onTaskTitleChange}
            onEnter={() => canSave && onSave()}
            disabled={!draft || isSaving}
          />
        )}

        {/* Never shrinks and never wraps: squeezed, the label broke into two lines and the button
            read as a small chip. The Details half is labelled: an unlabelled icon was not found. */}
        <div className="flex h-12 shrink-0 items-stretch overflow-hidden rounded-[30px] bg-[#E6F0FB] text-[#004494]">
          <button
            type="button"
            onClick={onSave}
            disabled={!canSave || isSaving}
            aria-busy={isSaving || undefined}
            title={
              source?.isLocked
                ? 'Dieser Eintrag läuft oder ist abgeschlossen – bitte über „Mit Details öffnen“ ändern.'
                : undefined
            }
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap px-6 font-[Roboto] text-[20px] font-bold leading-5 transition duration-200 hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#004494] disabled:cursor-not-allowed disabled:opacity-70"
          >
            {isSaving && <LoaderCircle className="h-5 w-5 animate-spin" aria-hidden />}
            {primaryLabel ?? 'Anlegen'}
          </button>

          <button
            type="button"
            onClick={onOpenForm}
            disabled={isWeekView || isSaving}
            aria-label="Mit Details öffnen"
            title="Mit Details öffnen"
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap border-l border-[#004494]/20 px-4 font-[Roboto] text-[16px] font-medium leading-5 transition duration-200 hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#004494] disabled:cursor-not-allowed disabled:opacity-70"
          >
            <PanelRightOpen className="h-5 w-5" strokeWidth={2.5} aria-hidden />
            Details
          </button>
        </div>

        <button
          type="button"
          onClick={plan.exit}
          aria-label="Planung abbrechen"
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white text-[#004494] transition duration-200 hover:bg-[#004494] hover:text-white"
        >
          <X className="h-6 w-6" strokeWidth={2.5} aria-hidden />
        </button>
      </div>
    </div>
  );
};
